import { Ban, Edit } from 'lucide-react'; // Icons for edit and block actions
import React, { useEffect, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import styled from 'styled-components';
import GenericModal from '../../components/GenericModal';
import GenericTable from '../../components/GenericTable';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { Game } from '../../models/Games';
import { getAllGames, saveGame, toggleGameStatus } from '../../redux/entities/gameEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import AddGamePage from './AddGamePage';

const PageHeading = styled.h2`
  margin-top: 3rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const AddButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;



const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 2rem;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionIcon = styled.div`
  cursor: pointer;
  color: #555;
  font-size: 1.5rem;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }
`;

const GamesPage: React.FC = () => {
  const [modalShow, setModalShow] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Game>({} as Game);
  const [searchQuery] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  const dispatch = useAppDispatch();
  let games = useAppSelector((state) => state.entities.gameReducer.games);
  const { showModalStrip } = useModalStrip();

  useEffect(() => {
    // Dispatch getAllGames action only if games are not already loaded
    if (!hasLoaded) {
      dispatch(getAllGames())
        .unwrap()
        .then(() => setHasLoaded(true))
        .catch((err: Error) => {
          showModalStrip('danger', err.message, 5000);
        });
    }
  }, [dispatch, hasLoaded, showModalStrip]);

  // Filtered data based on search query
  const filteredData = games?.filter((item) => {
    const searchValue = searchQuery.toLowerCase();
    return item.name.toLowerCase().includes(searchValue);
  });

  const columns = [
    { header: 'Game Name', field: 'name' },
    {
      header: 'Single Patti Game', field: 'singalPattiSupported', render: (rowData: Game) => {
        return rowData.singlePattiSupported ? 'Y' : 'N'
      }
    },
    {
      header: 'Digit Supported', field: 'digitSupported', render: (rowData: Game) => {
        return rowData.digitSupported ? 'Y' : 'N'
      }
    },
    {
      header: 'Barfi Game', field: 'burfiSupported', render: (rowData: Game) => {
        return rowData.burfiSupported ? 'Y' : 'N'
      }
    },
    // { header: 'Offline', field: 'offline' , render: (rowData: Game) => {
    //   return rowData.offline ? 'Y': 'N'
    // }},
    { header: 'Actions', field: 'actions' },
  ];

  const handleToggleStatus = (item: any) => {
    const response = window.confirm("Are you sure, you want to block the game?");
    if (response) {
      dispatch(toggleGameStatus(item.id))
        .unwrap()
        .catch((err: any) => {
          showModalStrip('danger', err.message, 5000);
        })
    }
  };

  const handleOpenModal = (item: any) => {
    setSelectedItem(item);
    setModalShow(true);
  };

  const handleSaveGame = (game: Game) => {
    dispatch(saveGame(game));
    setModalShow(false);
  };

  const isInactive = (item: any) => {
    return item?.status === 'INACTIVE';  // Assuming 'status' is a field in the data
  }

  return (
    <>
      {(
        <Container>
          <PageHeading>Games</PageHeading>

          {/* Add New User Button and Search Box */}
          <AddButtonContainer>
            <Button onClick={() => handleOpenModal({})}>Add Game</Button>
          </AddButtonContainer>

          {/* Table Wrapper */}
          <TableWrapper>
            <GenericTable
              columns={columns}
              data={filteredData?.map((item) => ({
                ...item,
                actions: (
                  <ActionButtonContainer>
                    <ActionIcon onClick={() => handleOpenModal(item)}>
                      <Edit />
                    </ActionIcon>
                    <ActionIcon onClick={() => handleToggleStatus(item)}>
                      <Ban color={isInactive(item) ? '#bb2124' : '#0d4a14'} />
                    </ActionIcon>
                  </ActionButtonContainer>
                ),
              }))}
              actions={false} // Don't display the generic edit and delete buttons, we are using custom actions
              pagination={true}
            />
          </TableWrapper>

          {modalShow && (
            <GenericModal
              isOpen={modalShow}
              onClose={() => setModalShow(false)}
              node={<AddGamePage data={selectedItem as Game} onSave={(game) => handleSaveGame(game)} />}
              label={selectedItem && selectedItem.id ? 'Update Game' : 'Add Game'}
            />
          )}
        </Container>
      )}
    </>
  );
};

export default GamesPage;
